import { useEffect } from "react"
import { useRouter, useSearchParams } from "next/navigation"

import { useCustomerContext, useGetCustomer } from "@unlikelystudio/react-ecommerce-hooks"

import { getCheckoutUrl } from "~/lib/shopify/utils/get-checkout-url"
import { useRedirectMultipassCheckoutUrl } from "~/hooks/checkout/useGetMultipassCheckoutUrl"
import { useTracking } from "~/providers/GTMTrackingProvider/hooks/use-tracking"

export default function useAuthentication(redirectOnError?: string, redirectOnSuccess?: string) {
  const router = useRouter()
  const searchParams = useSearchParams()
  const { mutate: handleCartConfirmation } = useRedirectMultipassCheckoutUrl()
  const { customerAccessToken } = useCustomerContext()

  const { sendEvent } = useTracking()

  useEffect(() => {
    redirectOnError && router.prefetch(redirectOnError as string)
    redirectOnSuccess && router.prefetch(redirectOnSuccess as string)
  }, [])

  const profileQuery = useGetCustomer({
    refetchOnMount: true,
    keepPreviousData: true,
    retry: 0,

    onSuccess: (data) => {
      const checkoutUrlPathname = searchParams.get("checkout_url")
      const checkoutUrl = checkoutUrlPathname ? getCheckoutUrl(checkoutUrlPathname) : undefined
      if (checkoutUrl && customerAccessToken) {
        handleCartConfirmation({
          url: checkoutUrl,
          accessToken: customerAccessToken,
        })
      } else {
        if (data && redirectOnSuccess) {
          sendEvent("login", { user_id: data?.id ?? "" })
          router.replace(redirectOnSuccess)
        }
        !data && redirectOnError && router.replace(redirectOnError)
      }
    },
  })

  const shouldRedirectOnError = profileQuery.fetchStatus === "idle" && !profileQuery.data && redirectOnError

  useEffect(() => {
    if (shouldRedirectOnError) {
      redirectOnError && router.replace(redirectOnError)
    }
  }, [shouldRedirectOnError])

  return profileQuery
}
